import React, {
  cloneElement,
  createContext,
  isValidElement,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import { IllustratedModalContent, type IllustratedModalContentProps } from "src/components/InnerModals";
import Modal, { type ModalProps as ModalPropsOriginal } from "src/components/Modal";
import ModalBox, { type ModalBoxProps as ModalBoxPropsOriginal } from "src/components/ModalBox";
import type { PopupProps } from "src/components/Popup";

import useContextSafe from "./useContextSafe";

type ModalProps = Omit<ModalPropsOriginal, "open" | "onClose"> & { children?: React.ReactNode };
type ModalBoxProps = Omit<ModalBoxPropsOriginal, "open" | "onClose"> & { children?: React.ReactNode };
type IllustratedModalProps = Pick<IllustratedModalContentProps, "header" | "text" | "children" | "image"> & {
  closeButton?: boolean;
  position?: PopupProps["position"];
  transparent?: boolean;
};
type HeadlessModalProps = {
  /** Render content directly so it can own and switch its presentation (modal, floating window, etc.). */
  headless: true;
  children: ReactNode;
};
export type ModalContent = ModalProps | ModalBoxProps | IllustratedModalProps | HeadlessModalProps;

interface ModalContextValue {
  /**
   * Set the content of the modal & show it
   *
   * Supports various types of modals (see the `ModalContent` type).
   */
  showModal: (modal: ModalContent | null) => void;

  /**
   * Dismiss the modal. Equivalent to calling `showModal(null)`
   */
  closeModal: () => void;

  /**
   * Returns true if there are any active modals
   */
  hasActiveModals: boolean;
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);
ModalContext.displayName = "ModalContext";

const ModalComponent = ({
  content,
  closeModal,
}: {
  content?: ModalContent | null;
  closeModal: () => void;
}) => {
  if (!content) {
    return null;
  } else if (isHeadlessModalProps(content)) {
    const child = content.children;
    if (isValidElement(child) && typeof child.type !== "string") {
      return cloneElement(child, { onClose: closeModal } as Record<string, unknown>);
    }
    return <>{child}</>;
  } else if (isModalProps(content)) {
    return <Modal {...content} open onClose={closeModal} />;
  } else if (isIllustratedModalProps(content)) {
    const { closeButton = true, position, transparent, ...props } = content;
    return (
      <ModalBox
        transparent={transparent}
        position={position}
        open
        onClose={closeModal}
        closeButton={closeButton}
      >
        <IllustratedModalContent actions={[]} {...props} />
      </ModalBox>
    );
  } else {
    return <ModalBox {...content} open onClose={closeModal} />;
  }
};

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  // If we end up adding a dismiss animation, we'll need to track the open/closed and content states
  // separately, so we can animate the dismissal without removing the content
  const [modalStack, setModalStack] = useState<Array<{ id: number; content: ModalContent | null }>>([]);
  const nextIdRef = React.useRef(0);

  const showModal = useCallback((modal: ModalContent | null) => {
    const id = nextIdRef.current++;
    setModalStack((prev) => [...prev, { id, content: modal }]);
  }, []);

  const closeModal = useCallback(() => {
    setModalStack((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      return prev.slice(0, -1);
    });
  }, []);

  const closeModalById = useCallback((id: number) => {
    setModalStack((prev) => {
      const index = prev.findIndex((modal) => modal.id === id);
      if (index === -1) return prev;
      return [...prev.slice(0, index), ...prev.slice(index + 1)];
    });
  }, []);

  const hasActiveModals = modalStack.length > 0;
  const modalContextValue = useMemo(
    () => ({ showModal, closeModal, hasActiveModals }),
    [showModal, closeModal, hasActiveModals]
  );

  return (
    <ModalContext.Provider value={modalContextValue}>
      {children}
      {modalStack.map((modal) => (
        <ModalComponent key={modal.id} closeModal={() => closeModalById(modal.id)} content={modal.content} />
      ))}
    </ModalContext.Provider>
  );
};

const isModalProps = (content: ModalContent): content is ModalProps => "message" in content;
const isHeadlessModalProps = (content: ModalContent): content is HeadlessModalProps =>
  "headless" in content && content.headless === true;
const isIllustratedModalProps = (content: ModalContent): content is IllustratedModalProps =>
  "image" in content;

/**
 * Allows you to show/dismiss modals.
 *
 * To use this hook, make sure `<ModalProvider />` is a parent/ancestor of your component.
 */
const useModal = () => useContextSafe(ModalContext);
export default useModal;
